import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box,
    Typography,
    TextField,
    Button,
    Link,
    CircularProgress,
    useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import Grid from '@mui/material/Grid2';
import axios from 'axios';
import { usePermissions } from '../hooks/usePermissions';

const inputSx = {
    backgroundColor: '#FFFFFF',
    borderRadius: '10px',
    boxShadow: 5,
    '& .MuiOutlinedInput-root': {
        borderRadius: '10px',
        '& fieldset': {
            borderColor: 'transparent',
        },
        '&:hover fieldset': {
            borderColor: 'rgba(0,0,0,0.15)',
        },
        '&.Mui-focused fieldset': {
            borderColor: 'rgba(0,0,0,0.25)',
            borderWidth: '1px',
        },
    },
    input: { fontSize: '16px' },
};

const LoginPage = () => {
    const navigate = useNavigate();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md'));
    const { refreshPermissions, isAuthenticated, isLoading: authLoading } = usePermissions();

    const [formData, setFormData] = useState({
        username: '',
        password: '',
    });
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');
    const API_URL = process.env.REACT_APP_API_URL;

    // Redirect to projects if already authenticated
    useEffect(() => {
        if (!authLoading && isAuthenticated) {
            navigate('/projects');
        }
    }, [authLoading, isAuthenticated, navigate]);

    // Initialize CSRF token
    useEffect(() => {
        const API_URL = process.env.REACT_APP_API_URL;
        const fetchData = async () => {
            await fetch(`${API_URL}/welcome`, {
                method: 'GET',
                credentials: 'include',
            });
        };
        fetchData();
    }, []);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prevState) => ({
            ...prevState,
            [name]: value,
        }));
        if (error) setError('');
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsLoading(true);
        setError('');

        try {
            let formDataToSend = new FormData();
            formDataToSend.append('username', formData.username);
            formDataToSend.append('password', formData.password);

            const response = await axios.post(`${API_URL}/login`, formDataToSend, {
                withCredentials: true,
                withXSRFToken: true,
            });

            if (response.status === 200) {
                await refreshPermissions();
                navigate('/projects');
            }
        } catch (error) {
            console.error('Login failed:', error);
            setError('Invalid email or password');
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <Grid container sx={{ minHeight: '100vh', width: '100%' }}>
            {/* Left panel - Gradient background with logo */}
            <Grid
                size={{ xs: 12, md: 6 }}
                sx={{
                    background: `linear-gradient(rgba(11, 21, 55, 0.75), rgba(11, 21, 55, 0.75)), url('/Moonlight-Connect-login-background.svg') #0B1537`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    minHeight: isMobile ? '200px' : '100vh',
                    p: 4,
                }}
            >
                <Box
                    component="img"
                    sx={{
                        width: isMobile ? '280px' : isTablet ? '500px' : '750px',
                    }}
                    src="/MOONLIGHT-quality-logo.svg"
                    alt="Moonlight Quality"
                />
            </Grid>

            {/* Right panel - Login form */}
            <Grid
                size={{ xs: 12, md: 6 }}
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'center',
                    padding: { xs: 3, sm: 4, md: 6 },
                    background: '#F3F3F3',
                }}
            >
                <Box
                    sx={{
                        textAlign: 'left',
                        width: '100%',
                        maxWidth: '400px',
                    }}
                >
                    <Typography
                        sx={{
                            mb: 3,
                            fontFamily: 'Montserrat, sans-serif',
                            fontWeight: 700,
                            fontSize: { xs: '1.5rem', sm: '1.75rem', md: '2rem' },
                        }}
                    >
                        SIGN IN
                    </Typography>
                </Box>

                {error && (
                    <Typography
                        color="error"
                        sx={{
                            mb: 2,
                            width: '100%',
                            maxWidth: '400px',
                            textAlign: 'left',
                        }}
                    >
                        {error}
                    </Typography>
                )}

                <Box
                    component="form"
                    onSubmit={handleSubmit}
                    noValidate
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        width: '100%',
                        maxWidth: '400px',
                    }}
                >
                    <TextField
                        placeholder="Email Address"
                        type="email"
                        name="username"
                        autoComplete="email"
                        autoFocus
                        value={formData.username}
                        onChange={handleChange}
                        margin="normal"
                        fullWidth
                        sx={inputSx}
                    />
                    <TextField
                        placeholder="Password"
                        type="password"
                        name="password"
                        autoComplete="current-password"
                        value={formData.password}
                        onChange={handleChange}
                        margin="normal"
                        fullWidth
                        sx={inputSx}
                    />

                    <Button
                        type="submit"
                        variant="contained"
                        disabled={isLoading}
                        fullWidth
                        sx={{
                            background: '#FBBB1D',
                            height: '56px',
                            mt: 3,
                            mb: 2,
                            fontFamily: 'Montserrat, sans-serif',
                            fontWeight: 700,
                            fontSize: { xs: 18, sm: 20 },
                            borderRadius: '10px',
                            boxShadow: 5,
                            color: '#fff',
                            '&:hover': {
                                background: '#E5AA15',
                            },
                            '&.Mui-disabled': {
                                background: '#FBBB1D',
                                opacity: 0.7,
                            },
                        }}
                    >
                        {isLoading ? (
                            <CircularProgress size={28} sx={{ color: '#fff' }} />
                        ) : (
                            'SIGN IN'
                        )}
                    </Button>
                </Box>

                {/* Links row */}
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        width: '100%',
                        maxWidth: '400px',
                    }}
                >
                    <Link
                        href="/forgot-password"
                        underline="hover"
                        sx={{
                            color: '#545454',
                            fontFamily: 'Montserrat, sans-serif',
                            fontSize: '0.875rem',
                            cursor: 'pointer',
                        }}
                    >
                        Forgot password?
                    </Link>
                    <Link
                        href="/phone-login"
                        underline="hover"
                        sx={{
                            color: '#545454',
                            fontFamily: 'Montserrat, sans-serif',
                            fontSize: '0.875rem',
                            cursor: 'pointer',
                        }}
                    >
                        Login with Phone
                    </Link>
                </Box>
            </Grid>
        </Grid>
    );
};

export default LoginPage;
